import { Navigate, Link } from "react-router-dom";
import { Camera, LayoutDashboard } from "lucide-react";

import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function HomePage() {
  const { user, loading, claim } = useAuth();

  // Redirect to landing if not authenticated
  if (!loading && !user) {
    return <Navigate to="/" replace />;
  }

  // Redirect to role-specific dashboard
  if (!loading && user && claim) {
    if (claim.role === "kioskconsumer") return <Navigate to="/kiosk" replace />;
    // Both admins and creators go to unified dashboard
    if (claim.role === "imagepackcreator" || claim.role === "admin") {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // User logged in but no claim - redirect to onboarding to claim role
  if (!loading && user && !claim) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="mx-auto max-w-5xl px-6 py-12">
        <div className="flex flex-col gap-4">
          <h1 className="text-balance text-4xl font-semibold tracking-tight">
            Memorix
          </h1>
          <p className="max-w-2xl text-pretty text-muted-foreground">
            AI-powered kiosk + digital souvenir platform for venues. Pick a
            scene, add cameos, style it, and instantly take home memories.
          </p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5" /> Kiosk
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>Touch-friendly stepper flow for guests.</p>
              <Button className="w-full" asChild size="lg">
                <Link to="/kiosk">Open Kiosk</Link>
              </Button>
              <Button className="w-full" variant="outline" asChild>
                <Link to="/kiosk/login">Kiosk Login</Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutDashboard className="h-5 w-5" /> Dashboard
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>Manage scene packs, cameos, team, settings, and more.</p>
              <Button className="w-full" asChild size="lg">
                <Link to="/dashboard">Open Dashboard</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
